// @workflow platform.viewer
/**
 * A page's own 3D viewer panel (the Registers page, the task board's side panel), registered by
 * the page while it is on screen, so the assistant's viewer tools use that panel in place instead
 * of taking the user to the facility's main view. The facility viewer page registers its panes
 * instead (panes.js); a page registers here only when it hosts ViewersSection beside its content.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */

/**
 * What the page registers. Each function is bound to the page; none of them is the component.
 * @typedef {object} PageViewer
 * @property {() => string|null} facilityId  the facility the page shows
 * @property {() => boolean} is3dOpen        the panel is open on its 3D tab
 * @property {() => Promise<*>} open3d       open the panel on its 3D tab and load the model there
 */

const PAGE_VIEWER_METHODS = Object.freeze(['facilityId', 'is3dOpen', 'open3d']);

function assertPageViewer(page) {
  const missing = PAGE_VIEWER_METHODS.filter((name) => !page || typeof page[name] !== 'function');
  if (missing.length) throw new TypeError(`registerPageViewer: missing ${missing.join(', ')}`);
}

/** One registry; the last page to register wins, and only that page can unregister itself. */
export function createPageViewerRegistry() {
  let current = null;
  return Object.freeze({
    /**
     * @param {PageViewer} page
     * @returns {() => void} unregister: clears the registration only while it is still this one
     */
    register(page) {
      assertPageViewer(page);
      const entry = Object.freeze({ ...page });
      current = entry;
      return () => {
        if (current === entry) current = null;
      };
    },
    /** @returns {PageViewer|null} */
    get() {
      return current;
    },
  });
}

/** The app's registry (one page on screen). */
export const pageViewers = createPageViewerRegistry();

/** @param {PageViewer} page @returns {() => void} unregister */
export const registerPageViewer = (page) => pageViewers.register(page);
